import {
    createRef,
    forwardRef,
    useImperativeHandle,
    useState,
} from 'react'

import EmploymentInput from './Components/EmploymentInput'
import EducationInput from './Components/EducationInput'
import IdentityInformation from './Components/IdentityInformation'
import AddressHistory from './Components/AddressHistory'
import Consent from './Components/Consent'
import ProfessionalReferenceInput from './Components/ProfessionalReferenceInput'
import AdditionalInformation from './Components/AdditionalInformation'

const ApplicantDataForm = forwardRef(({ formFields, data, currentIndexHandler, consentData }, ref) => {

    const formData = data || {}

    const fieldRefs = formFields.map(_ => createRef())

    const [currentIndex, setCurrentIndex] = useState(0)

    const [maxSelectableIndex, setMaxSelectableIndex] = useState(0)

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {

            const fieldResults = fieldRefs.map((fieldRef) => fieldRef.current.validateAndGetData())
            const error = fieldResults.some((result) => !result.isValid)

            const result = {}

            for (const [index, field] of formFields.entries()) {
                result[field.type] = fieldResults[index].value
            }

            return {
                isValid: !error,
                value: result,
            }
        },
    }))

    const handleSetCurrentIndex = (index) => {

        let maxIndex = 0

        for (const [index, fieldRef] of fieldRefs.entries()) {
            maxIndex = index
            const isValid = fieldRef.current.validateAndGetData().isValid
            if (!isValid) {
                break
            }
        }
        if (maxIndex !== maxSelectableIndex) {
            setMaxSelectableIndex(maxIndex)
        }

        if (index > maxIndex) {
            return
        }
        setCurrentIndex(index)

        currentIndexHandler?.(index)

    }

    return (
        <div className="flex flex-col w-full max-w-6xl mx-auto my-8 p-2 md:p-6 bg-white shadow rounded-xl space-y-8 border border-gray-300">
            <div className="flex-1 px-6 md:px-20">

                <div className='mb-4' >
                    <Consent />
                </div>

                {formFields.map((formField, index) => (
                    <div key={index} >

                        {
                            formField.type === "identity_information" &&
                            <IdentityInformation
                                title={formField.title}
                                ref={fieldRefs[index]}
                                attributes={formField.attributes}
                                data={formData[formField.key]}
                                isSelected={currentIndex === index}
                                handleFieldSelection={() => handleSetCurrentIndex(index)}
                            />
                        }
                        {
                            formField.type === "address_history" &&
                            <AddressHistory
                                title={formField.title}
                                ref={fieldRefs[index]}
                                arrayOf={formField.arrayOf}
                                data={formData[formField.key]}
                                isSelected={currentIndex === index}
                                handleFieldSelection={() => handleSetCurrentIndex(index)}
                            />
                        }
                        {
                            formField.type === "employment_history" &&
                            <EmploymentInput
                                title={formField.title}
                                ref={fieldRefs[index]}
                                arrayOf={formField.arrayOf}
                                data={formData[formField.key]}
                                isSelected={currentIndex === index}
                                handleFieldSelection={() => handleSetCurrentIndex(index)}
                            />
                        }
                        {
                            formField.type === "education_history" &&
                            <EducationInput
                                title={formField.title}
                                ref={fieldRefs[index]}
                                arrayOf={formField.arrayOf}
                                data={formData[formField.key]}
                                isSelected={currentIndex === index}
                                handleFieldSelection={() => handleSetCurrentIndex(index)}
                            />
                        }
                        {
                            formField.type === "professional_reference" &&
                            <ProfessionalReferenceInput
                                title={formField.title}
                                ref={fieldRefs[index]}
                                arrayOf={formField.arrayOf}
                                atLeastCount={formField.atLeastCount}
                                data={formData[formField.key]}
                                isSelected={currentIndex === index}
                                handleFieldSelection={() => handleSetCurrentIndex(index)}
                            />
                        }
                        {
                            formField.type === "additional_information" &&
                            <AdditionalInformation
                                title={formField.title}
                                ref={fieldRefs[index]}
                                attributes={formField.attributes}
                                data={formData[formField.key]}
                                isSelected={currentIndex === index}
                                handleFieldSelection={() => handleSetCurrentIndex(index)}
                            />
                        }
                    </div>
                ))}

            </div>

        </div>
    )
})

export default ApplicantDataForm
